import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { UerActKc3Query } from 'gql/graphql';
import type { TActionCommonInterface } from 'utils/types';

import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { uerActKC3ActsKC2SelectionListGql, uerActKC3Gql } from '../addOrEditActKc3Queries';

interface IUseWorkOrderActsKC2Data {
    contractId: number;
    actKC3Id?: number;
    isReadonly?: boolean;
    filterValues?: Record<string, string[]>;
    rootContractObjectId?: number;
    workOrderId?: number;
}

interface IActKC3FormData {
    number: number;
    fullNumber?: string | null;
    date: Date;
    reportingStartDate: Date;
    reportingFinishDate: Date;
    actKC2Ids: any;
}

const checkedReducer = (acc: any, item: any) => {
    acc[item?.id || item] = true;

    return acc;
};

const getWhere = (filterValues?: Record<string, string[]>) =>
    Object.entries(filterValues ?? {})
        .filter(([, value]) => value.length)
        .map(([name, value]) => ({
            [name]: {
                in: value,
            },
        }));

export const useActKC3Data = (props: IUseWorkOrderActsKC2Data) => {
    const { contractId, actKC3Id, isReadonly, filterValues, rootContractObjectId, workOrderId } = props;
    const dispatch = useDispatch();

    const selectionListVariables = useMemo(
        () => ({
            rootContractObjectId,
            workOrderId,
            contractId,
            actKC3Id,
            ...(isReadonly
                ? {
                      where: {
                          and: [
                              {
                                  isSelected: {
                                      eq: true,
                                  },
                              },
                              ...getWhere(filterValues),
                          ],
                      },
                  }
                : {
                      where: {
                          and: getWhere(filterValues),
                      },
                  }),
        }),
        [actKC3Id, contractId, filterValues, isReadonly, rootContractObjectId, workOrderId],
    );

    const [selectionListData] = useRequest({
        requestId: 'actKC3SelectionList',
        query: uerActKC3ActsKC2SelectionListGql,
        withEffect: Boolean(contractId),
        variables: selectionListVariables,
    });

    const actKc3DataVariables = useMemo<any>(
        () => ({
            actKC3Id,
        }),
        [actKC3Id],
    );

    const onSetActKC3Info = useCallback((params: TActionCommonInterface<UerActKc3Query>) => {
        const { number, fullNumber, date, reportingStartDate, reportingFinishDate, actKC2Ids } =
            params?.payload?.uerActKC3?.data ?? {};

        setFormData({
            number,
            fullNumber,
            date: date && new Date(date),
            reportingStartDate: reportingStartDate && new Date(reportingStartDate),
            reportingFinishDate: reportingFinishDate && new Date(reportingFinishDate),
            actKC2Ids: actKC2Ids?.reduce(checkedReducer, {}),
        });
    }, []);

    useRequest({
        requestId: 'actKc3Info',
        query: uerActKC3Gql,
        withEffect: Boolean(actKC3Id),
        variables: actKc3DataVariables,
        dependenciesUpdater: onSetActKC3Info,
    });

    const [formData, setFormData] = useState<IActKC3FormData | null>(null);

    const [resetSelectionList] = useResetStoreData({ requestId: 'actKC3SelectionList' });
    const [resetActKc3Data] = useResetStoreData({ requestId: 'actKc3Info' });

    const resetData = useCallback(() => {
        dispatch(resetSelectionList(undefined));
        if (actKC3Id) {
            dispatch(resetActKc3Data(undefined));
        }
    }, [actKC3Id, dispatch, resetActKc3Data, resetSelectionList]);

    return { formData, rowData: selectionListData?.uerActKC3ActsKC2SelectionList?.items, resetActKc3Data: resetData };
};
